import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { add } from '@adv-frontend/yastore-modules/lib';
import {
    BookOrderMethod,
    type BookOrderPathParameters,
    type BookOrderResponseSuccess,
    type BookOrderSearchParams,
    getBookOrderUrl,
    getGetOrderUrl,
    GetOrderMethod,
    type GetOrderPathParameters,
    type GetOrderSearchParams,
    getUpdateOrderItemsUrl,
    getUpdateOrderUrl,
    getUpdateSnapshotUrl,
    type SchemaOrderWithDeliveryOptions,
    type SchemaPaymentContext,
    UpdateOrderItemsMethod,
    type UpdateOrderItemsPathParameters,
    type UpdateOrderItemsRequest,
    type UpdateOrderItemsSearchParams,
    UpdateOrderMethod,
    UpdateSnapshotMethod,
    type UpdateSnapshotPathParameters,
    type UpdateSnapshotSearchParams,
} from '@/shared/api/openApi';
import { OrderAcquiringTypes, type OrderDto, type OrderPersistenceDto } from '@/shared/api/order';
import { OrderPaymentMethods } from '@/shared/api/order';
import { request } from '@/shared/api/request';
import { nativePayEnabled } from '@/shared/lib/helpers';
import { getRequestContext } from '@/shared/lib/requestContext';
import {
    InvalidOrderError,
    MinimumOrderAmountNotMetError,
    OrderInvalidDeliveryFloorError,
    OrderInvalidEmailError,
    OrderInvalidPhoneError,
    OrderLoyaltyApplyError,
    OrderLoyaltyConditionsChangedError,
    OrderMismatchedDeliveryPriceError,
    OrderMismatchedItemPriceError,
    OrderPaymentError,
    OrderReserveStocksError,
    PromoCodeApplyError,
    PromoCodeUsageLimitExceededError,
} from '../errors';
import { endUpdateOrder, startUpdateOrder } from '../slices';
import type { UpdateOrderItemsResponse, UpdateOrderRequestParams } from '../types';
import { getPaymentContext } from './paymentApi';

type GetOrderRequestParams = GetOrderSearchParams & GetOrderPathParameters;
export type UpdateOrderItemsRequestParams = UpdateOrderItemsRequest &
    UpdateOrderItemsSearchParams &
    UpdateOrderItemsPathParameters;
type UpdateSnapshotRequestParams = UpdateSnapshotSearchParams & UpdateSnapshotPathParameters;

export const upsertGetOrderQueryData = <T extends OrderDto>(
    response: T,
    _meta: {} | undefined,
    arg: GetOrderRequestParams
) => {
    api.endpoints.GetOrder.upsertQueryData(
        {
            store_slug: arg.store_slug,
            order_id: arg.order_id,
            signature: arg.signature,
        },
        response
    );

    return response;
};

export const api = createApi({
    name: 'entities/order/api/api',
    baseQuery: createBaseQuery(request),
    endpoints: (builder) => {
        return {
            GetOrder: builder.query<OrderDto, GetOrderRequestParams>({
                query: (payload) => {
                    return {
                        url: getGetOrderUrl(payload),
                        method: GetOrderMethod,
                        params: { signature: payload.signature },
                    };
                },
            }),

            UpdateOrder: builder.mutation<OrderDto, UpdateOrderRequestParams>({
                query: ({ signature, storeSlug, ...payload }) => {
                    return {
                        url: getUpdateOrderUrl({
                            order_id: payload.orderMeta.order_id,
                            store_slug: storeSlug,
                        }),
                        method: UpdateOrderMethod,
                        body: {
                            ...payload.orderMeta,
                            timestamp: Date.now(),
                        },
                        params: { signature },
                    };
                },
                onQueryStarted: async (_, { queryFulfilled }) => {
                    try {
                        startUpdateOrder();
                        await queryFulfilled;
                    } finally {
                        endUpdateOrder();
                    }
                },
                transformResponse: (response: OrderDto, meta, arg) =>
                    upsertGetOrderQueryData(response, meta, {
                        order_id: arg.orderMeta.order_id,
                        signature: arg.signature,
                        store_slug: arg.storeSlug,
                    }),
            }),

            BookOrder: builder.mutation<BookOrderResponseSuccess, BookOrderSearchParams & BookOrderPathParameters>({
                query: (payload) => {
                    return {
                        url: getBookOrderUrl(payload),
                        method: BookOrderMethod,
                        params: { signature: payload.signature },
                    };
                },
            }),

            UpdateOrderItems: builder.mutation<UpdateOrderItemsResponse, UpdateOrderItemsRequestParams>({
                query: (payload) => {
                    return {
                        url: getUpdateOrderItemsUrl(payload),
                        method: UpdateOrderItemsMethod,
                        params: { signature: payload.signature },
                        body: {
                            items: payload.items,
                        },
                    };
                },
                transformResponse: (response: UpdateOrderItemsResponse, meta, arg) => {
                    if (response.is_success) {
                        upsertGetOrderQueryData(response.order, meta, {
                            order_id: arg.order_id,
                            signature: arg.signature,
                            store_slug: arg.store_slug,
                        });
                    }

                    return response;
                },
            }),

            UpdateSnapshot: builder.mutation<OrderDto & SchemaOrderWithDeliveryOptions, UpdateSnapshotRequestParams>({
                query: (payload) => {
                    return {
                        url: getUpdateSnapshotUrl(payload),
                        method: UpdateSnapshotMethod,
                        params: { signature: payload.signature },
                    };
                },
                transformResponse: (response: OrderDto & SchemaOrderWithDeliveryOptions, meta, arg) => {
                    upsertGetOrderQueryData(response.order as unknown as OrderDto, meta, {
                        order_id: arg.order_id,
                        signature: arg.signature,
                        store_slug: arg.store_slug,
                    });

                    return response;
                },
            }),
        };
    },
});

export const { useLazyGetOrderQuery, useGetOrderQuery } = api;
export const getOrderQuery = api.endpoints.GetOrder.query;

export const bookOrder = api.endpoints.BookOrder.mutate;
export const updateOrderItems = api.endpoints.UpdateOrderItems.mutate;
export const updateOrder = api.endpoints.UpdateOrder.mutate;

export const updateOrderSnapshot = api.endpoints.UpdateSnapshot.mutate;

function throwBookOrderError(response: BookOrderResponseSuccess): never {
    switch (response.error) {
        case 'ERROR_RESERVE_STOCKS':
            throw new OrderReserveStocksError(response.insufficient_items);
        case 'ERROR_INVALID_PHONE':
            throw new OrderInvalidPhoneError();
        case 'ERROR_INVALID_EMAIL':
            throw new OrderInvalidEmailError();
        case 'ERROR_MISMATCHED_PRICE_ITEMS':
            throw new OrderMismatchedItemPriceError();
        case 'ERROR_MISMATCHED_DELIVERY_PRICE':
            throw new OrderMismatchedDeliveryPriceError();
        case 'ERROR_MINIMUM_ORDER_AMOUNT_NOT_MET':
            throw new MinimumOrderAmountNotMetError();
        case 'ERROR_PROMOCODE_USAGE_LIMIT_EXCEEDED':
            throw new PromoCodeUsageLimitExceededError();
        case 'ERROR_PROMOCODE_HAS_NO_APPLICABLE_ITEMS':
            throw new PromoCodeApplyError('В корзине нет подходящих товаров');
        case 'ERROR_ORDER_LOYALTY_CONDITIONS_CHANGED':
            throw new OrderLoyaltyConditionsChangedError();
        case 'ERROR_ORDER_LOYALTY_FAILED_TO_APPLY':
            throw new OrderLoyaltyApplyError();
        case 'ERROR_DELIVERY_FLOOR_IS_REQUIRED':
            throw new OrderInvalidDeliveryFloorError();

        default:
            throw new InvalidOrderError(response.error_descr);
    }
}

async function processCloudPayment(options: SchemaPaymentContext['options'], storeUrl?: string): Promise<boolean> {
    const widget = new window.cp.CloudPayments({});
    const paymentResponse = await widget.pay(
        'charge',
        {
            ...options,
            configuration: {
                common: {
                    successRedirectUrl: storeUrl,
                },
            },
            amount: Number(options?.amount),
        },
        {
            onSuccess: storeUrl,
        }
    );

    if (paymentResponse.status === 'success') {
        return true;
    }

    if (paymentResponse.status === 'fail') {
        throw new OrderPaymentError();
    }

    return false;
}

export async function proceedOrderPayment(
    orderPersistenceDto: OrderPersistenceDto,
    signature: string,
    storeSlug: string,
    storeUrl?: string,
    order?: OrderDto
): Promise<boolean> {
    const {
        account,
        yandexPay: { nativePayDisabled, yandexPayAvailableForAnonymous },
    } = getRequestContext();

    const useNativePay = nativePayEnabled({
        yandexPayAvailableForAnonymous,
        nativePayDisabled,
        loggedIn: account.loggedIn,
        paymentMethod: orderPersistenceDto.payment_method,
    });

    await api.endpoints.UpdateOrder.mutate({
        orderMeta:
            orderPersistenceDto.payment_method === 'CARD_ONLINE' &&
            add(order?.total_final_price || 0, order?.delivery.final_customer_costs) > 0
                ? {
                      ...orderPersistenceDto,
                      acquiring_type: useNativePay ? OrderAcquiringTypes.YandexPay : OrderAcquiringTypes.CloudPay,
                  }
                : orderPersistenceDto,
        signature,
        storeSlug,
    });

    const bookOrderResponse = await api.endpoints.BookOrder.mutate({
        order_id: orderPersistenceDto.order_id,
        signature,
        store_slug: storeSlug,
    });

    // Возникли проблемы с резервацией товаров: изменились цены или больше нет доступных товаров
    if (!bookOrderResponse.isSuccess) {
        throwBookOrderError(bookOrderResponse);
    }

    if (orderPersistenceDto.payment_method !== OrderPaymentMethods.CARD_ONLINE) {
        // Оплата онлайн не требуется, добавить редирект на окно успешного оформления заказа
        return true;
    }

    const paymentContext = useNativePay
        ? undefined
        : await getPaymentContext({
              order_id: orderPersistenceDto.order_id,
              signature,
              store_slug: storeSlug,
              // TODO SS-5625 Убрать как бэк научится определять залогинен ли пользователь
              logged_in: account.loggedIn,
              on_success_redirect: window.location.href,
              on_error_redirect: window.location.href,
              on_abort_redirect: window.location.href,
          });

    // Оплата с помощью Яндекс Пэй или другим сервисом во внешней системе
    if (paymentContext?.url || useNativePay) {
        if (paymentContext?.acquiring_type === OrderAcquiringTypes.YandexPay && useNativePay) {
            return true;
        }

        if (paymentContext?.url) {
            window.location.href = paymentContext.url;
        }

        return true;
    }

    // Оплата с помощью CloudPayments
    return processCloudPayment(paymentContext?.options, storeUrl);
}
